import React from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

function OrderConfirmationModal({ isOpen, onClose, onConfirm, orderDetails }) {
  const { colors } = useTheme();

  if (!isOpen) return null;

  const isBuy = orderDetails?.order_type === 'BUY';
  const accentColor = isBuy ? '#6366F1' : '#EF4444';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
    >
      <div
        className="app-card w-full max-w-md rounded-2xl p-6"
        style={{ background: `linear-gradient(135deg, ${colors.accent}40 0%, ${colors.accent}15 100%), ${colors.cardBg}`, border: `1px solid ${colors.accentBorder}` }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-6 h-6" style={{ color: accentColor }} />
            <h2 className="text-xl font-bold" style={{ color: colors.primaryText }}>
              Confirm Order
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full"
            style={{ backgroundColor: colors.inputBg }}
          >
            <X className="w-5 h-5" style={{ color: colors.secondaryText }} />
          </button>
        </div>

        {/* Order Details */}
        <div
          className="p-4 rounded-xl space-y-3 mb-6"
          style={{ backgroundColor: colors.inputBg }}
        >
          <div className="flex justify-between items-center">
            <span style={{ color: colors.secondaryText }}>Stock</span>
            <span className="font-bold text-lg" style={{ color: colors.primaryText }}>
              {orderDetails?.symbol}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span style={{ color: colors.secondaryText }}>Order Type</span>
            <span
              className="font-semibold px-3 py-1 rounded-full"
              style={{
                backgroundColor: `${accentColor}15`,
                color: accentColor,
              }}
            >
              {orderDetails?.order_type}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span style={{ color: colors.secondaryText }}>Quantity</span>
            <span className="font-semibold" style={{ color: colors.primaryText }}>
              {orderDetails?.quantity} shares
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span style={{ color: colors.secondaryText }}>Price</span>
            <span className="font-semibold" style={{ color: colors.primaryText }}>
              ₹{orderDetails?.price}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span style={{ color: colors.secondaryText }}>Duration</span>
            <span className="font-semibold" style={{ color: colors.primaryText }}>
              {orderDetails?.duration || 'Intraday'}
            </span>
          </div>

          {orderDetails?.stoploss && (
            <div className="flex justify-between items-center">
              <span style={{ color: colors.secondaryText }}>Stoploss</span>
              <span className="font-semibold" style={{ color: colors.loss }}>
                ₹{orderDetails.stoploss}
              </span>
            </div>
          )}

          {orderDetails?.target && (
            <div className="flex justify-between items-center">
              <span style={{ color: colors.secondaryText }}>Target</span>
              <span className="font-semibold" style={{ color: colors.profit }}>
                ₹{orderDetails.target}
              </span>
            </div>
          )}

          <div
            className="pt-3 mt-3 flex justify-between items-center font-bold"
            style={{ borderTop: `1px solid ${colors.border}` }}
          >
            <span style={{ color: colors.primaryText }}>Total Value</span>
            <span className="text-lg" style={{ color: accentColor }}>
              ₹{(orderDetails?.price * orderDetails?.quantity).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Warning */}
        <div
          className="flex items-start space-x-2 p-3 rounded-lg mb-6"
          style={{ backgroundColor: `${colors.accent}10` }}
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.accent }} />
          <p className="text-xs" style={{ color: colors.secondaryText }}>
            Order will be queued and executed at next market open (9:15 AM IST)
          </p>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onClose}
            className="py-3 rounded-xl font-semibold"
            style={{
              backgroundColor: colors.inputBg,
              color: colors.primaryText,
              borderWidth: '1px',
              borderColor: colors.border,
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="py-3 rounded-xl font-semibold text-white"
            style={{ backgroundColor: accentColor }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderConfirmationModal;
